/**
 * Migration Studio — Phase A.1 UI
 *
 * Shows all 13 sections from the spec. Section 1 (Projects) is FULLY WORKING
 * against the Phase A.1 backend. Sections 2-13 render honest "Coming in
 * Phase A.N" placeholders — never fake buttons, never mock progress.
 *
 * Every navigation change updates the URL via a hash fragment so links are
 * shareable within the module without needing 13 separate React routes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FolderKanban,
  Upload,
  Search,
  Link2,
  Layers,
  FileWarning,
  BarChart3,
  ShieldCheck,
  ClipboardList,
  BookMarked,
  Settings2,
  PlusCircle,
  RefreshCw,
  Loader2,
  Lock,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  FileText,
  Trash2,
  Eye,
  XCircle,
  AlertTriangle,
  Database,
  ScrollText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  CostingPane,
  ControlTowerPane,
  TestMigrationPane,
  ReconciliationPane,
  ReleasePane,
  HypercarePane,
} from './MigrationStudioLayerB';
import LegacyExtractorPane from './migration/LegacyExtractorPane';
import CompliancePane, { ComplianceSeals } from './migration/CompliancePane';
import RetailIntegrityPane from './migration/RetailIntegrityPane';
import { StudioTour, StudioTourReplayButton } from './MigrationStudioTour';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.12.8 — Country/currency pill that surfaces the tenant's active
// country profile. Silently absent when no profile is set (never blocks
// the Studio for tenants that operate globally without a country tag).
function CountryPill() {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/country-profiles/effective/for-tenant`)
      .then((r) => setProfile(r?.data?.profile))
      .catch(() => setProfile(null));
  }, []);
  if (!profile) return null;
  const flag = COUNTRY_FLAGS[profile.country_code] || '🌐';
  return (
    <Badge
      className="bg-blue-50 text-blue-800 border-blue-200 text-[10px]"
      title={`Tenant country: ${profile.country_name} · ${profile.tax_name} ${profile.tax_rate_pct}% · ${profile.tax_authority}`}
      data-testid="studio-country-pill"
    >
      {flag} {profile.country_code} · {profile.currency_code} · {profile.tax_name} {profile.tax_rate_pct}%
    </Badge>
  );
}

const COUNTRY_FLAGS = {
  BH: '🇧🇭', SA: '🇸🇦', AE: '🇦🇪', KW: '🇰🇼', QA: '🇶🇦', OM: '🇴🇲',
  PH: '🇵🇭', SG: '🇸🇬', MY: '🇲🇾', ID: '🇮🇩',
  US: '🇺🇸', GB: '🇬🇧', AU: '🇦🇺', DE: '🇩🇪', FR: '🇫🇷', IN: '🇮🇳',
};

// ── Section registry — order matters (mirrors the spec's numbering) ─────────
const SECTIONS = [
  { id: 'projects',      label: 'Migration Projects',   Icon: FolderKanban, phase: 'A.1', live: true  },
  { id: 'new',           label: 'New Project Wizard',   Icon: PlusCircle,   phase: 'A.1', live: true  },
  { id: 'dashboard',     label: 'Project Dashboard',    Icon: BarChart3,    phase: 'A.1', live: true  },
  { id: 'sources',       label: 'Data Sources',         Icon: Upload,       phase: 'A.2', live: true  },
  { id: 'legacy',        label: 'Legacy Extractor',     Icon: Database,     phase: 'A.2', live: true  },
  { id: 'discovery',     label: 'Discovery & Profiling',Icon: Search,       phase: 'A.3', live: true  },
  { id: 'mapping',       label: 'Mapping Workbench',    Icon: Layers,       phase: 'A.4', live: true  },
  { id: 'dictionary',    label: 'Canonical Registry',   Icon: BookMarked,   phase: 'A.4', live: true  },
  { id: 'gaps',          label: 'Gaps & Proposals',     Icon: FileWarning,  phase: 'A.5', live: true  },
  { id: 'coverage',      label: 'Coverage Report',      Icon: BarChart3,    phase: 'A.5', live: true  },
  { id: 'trust',         label: 'Trust Center',         Icon: ShieldCheck,  phase: 'A.6', live: true  },
  { id: 'compliance',    label: 'Compliance Registry',  Icon: ScrollText,   phase: 'A.6', live: true  },
  { id: 'ledger',        label: 'Access Ledger',        Icon: ClipboardList,phase: 'A.1', live: true  },
  { id: 'costing',       label: 'Costing & Quote',      Icon: FileText,     phase: 'B.1', live: true  },
  { id: 'control',       label: 'Control Tower',        Icon: BarChart3,    phase: 'B.2', live: true  },
  { id: 'test',          label: 'Test Migration',       Icon: Sparkles,     phase: 'B.3', live: true  },
  { id: 'reconcile',     label: 'Reconciliation',       Icon: CheckCircle2, phase: 'B.4', live: true  },
  { id: 'retail',        label: 'Retail Integrity',     Icon: ShieldCheck,  phase: 'B.4', live: true  },
  { id: 'release',       label: 'UAT & Release',        Icon: CheckCircle2, phase: 'B.5', live: true  },
  { id: 'hypercare',     label: 'Rollback & Hypercare', Icon: AlertTriangle,phase: 'B.6', live: true  },
];

// ── Layer B disabled navigation (per spec §"DEFERRED NAVIGATION") ───────────
const LAYER_B_ITEMS = [
  { key: 'costing_enabled',                 label: 'Costing & Quote',        arrives: 'Layer B' },
  { key: 'migration_control_tower_enabled', label: 'Migration Control Tower',arrives: 'Layer B' },
  { key: 'test_migration_enabled',          label: 'Test Migration',         arrives: 'Layer B' },
  { key: 'production_release_enabled',      label: 'UAT & Release',          arrives: 'Layer B' },
  { key: 'rollback_enabled',                label: 'Rollback & Hypercare',   arrives: 'Layer B' },
];

// ── main component ─────────────────────────────────────────────────────────

export default function MigrationStudioPage() {
  const [section, setSection] = useState(() => {
    const hash = window.location.hash.slice(1);
    return SECTIONS.find((s) => s.id === hash)?.id || 'projects';
  });
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    window.location.hash = section;
  }, [section]);

  const currentMeta = SECTIONS.find((s) => s.id === section) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="migration-studio-page">
      <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        <header className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
                Konnect Migration Studio
              </span>
              <Badge className="bg-slate-900 text-white text-[10px]">Layers A + B · Fully Live</Badge>
              <CountryPill />
              <ComplianceSeals onOpenPane={() => setSection('compliance')} />
              <StudioTourReplayButton />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Legacy-to-cloud onboarding, done transparently.
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 max-w-2xl">
              Upload legacy exports. We profile, map, and propose customisations. You approve every high-risk decision.
              Production writes are gated behind explicit UAT sign-off + tenant opt-in.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-4 sm:gap-6">
          {/* left rail */}
          <SectionRail current={section} onSelect={setSection} />

          {/* Onboarding tour overlay (first-visit auto-trigger, `?` replay-button) */}
          <StudioTour onFocusSection={setSection} />

          {/* right pane */}
          <div className="space-y-4">
            {section === 'projects'     && <ProjectsPane onOpenProject={(pid) => { setActiveProject(pid); setSection('sources'); }} />}
            {section === 'new'          && <NewProjectPane onCreated={() => setSection('projects')} onOpenTrust={() => setSection('trust')} />}
            {section === 'dashboard'    && <DashboardPane />}
            {section === 'sources'      && <DataSourcesPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'legacy'       && <LegacyExtractorPane activeProject={activeProject} onExtractImported={() => setSection('sources')} />}
            {section === 'compliance'   && <CompliancePane />}
            {section === 'discovery'    && <DiscoveryPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'mapping'      && <MappingPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'dictionary'   && <CanonicalRegistryPane />}
            {section === 'gaps'         && <GapsPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'coverage'     && <CoveragePane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'trust'        && <TrustCenterPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'ledger'       && <AccessLedgerPane />}
            {section === 'costing'      && <CostingPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'control'      && <ControlTowerPane />}
            {section === 'test'         && <TestMigrationPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'reconcile'    && <ReconciliationPane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'retail'       && <RetailIntegrityPane activeProject={activeProject} />}
            {section === 'release'      && <ReleasePane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {section === 'hypercare'    && <HypercarePane activeProject={activeProject} setActiveProject={setActiveProject} />}
            {!currentMeta.live && <ComingSoonPane meta={currentMeta} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── left rail ──────────────────────────────────────────────────────────────

function SectionRail({ current, onSelect }) {
  return (
    <nav
      className="bg-white rounded-xl border border-slate-200 p-2 h-fit lg:sticky lg:top-4 overflow-x-auto lg:overflow-visible flex lg:block whitespace-nowrap lg:whitespace-normal"
      data-testid="migration-rail"
    >
      {SECTIONS.map((s) => {
        const active = s.id === current;
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            data-testid={`rail-${s.id}`}
            className={`shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 py-2 rounded-lg text-xs sm:text-sm text-left transition-colors ${
              active ? 'bg-emerald-50 text-emerald-800 font-semibold' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <s.Icon className="w-4 h-4 flex-shrink-0" />
            <span className="lg:flex-1 lg:truncate">{s.label}</span>
            {!s.live && (
              <Badge className="bg-slate-100 text-slate-500 text-[10px] font-normal border">
                {s.phase}
              </Badge>
            )}
          </button>
        );
      })}
      <div className="hidden lg:block border-t border-slate-200 mt-2 pt-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 px-3 pb-1">
          Layer B — live
        </div>
      </div>
    </nav>
  );
}

// ── generic coming-soon pane ───────────────────────────────────────────────

function ComingSoonPane({ meta }) {
  return (
    <Card className="border-dashed border-2 border-slate-300 bg-white" data-testid={`coming-soon-${meta.id}`}>
      <CardContent className="p-10 text-center space-y-3">
        <meta.Icon className="w-12 h-12 mx-auto text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-800">{meta.label}</h2>
        <div className="flex items-center gap-2 justify-center">
          <Badge className="bg-slate-900 text-white">Arrives in Phase {meta.phase}</Badge>
        </div>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          This section is architected on the backend (models + endpoints scaffolded with 501 responses)
          but not activated in Phase A.1. Nothing here is a fake or mocked feature.
        </p>
      </CardContent>
    </Card>
  );
}

// ── projects list ──────────────────────────────────────────────────────────

function ProjectsPane({ onOpenProject }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects`);
      setProjects(data.projects || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="projects-pane">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Migration Projects</h2>
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-projects-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {projects.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-10 text-center space-y-2">
            <FolderKanban className="w-10 h-10 mx-auto text-slate-300" />
            <div className="text-slate-600">No migration projects yet.</div>
            <div className="text-sm text-slate-500">Use the &ldquo;New Project Wizard&rdquo; section to create one.</div>
          </CardContent>
        </Card>
      )}

      {projects.map((p) => (
        <Card key={p._id} className="border-slate-200 hover:border-emerald-300 transition-colors cursor-pointer" data-testid={`project-row-${p._id}`} onClick={() => onOpenProject?.(p._id)}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{p.project_number}</span>
                <span className="font-semibold text-slate-900">{p.project_name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {p.optional_business_type || 'unspecified'} · {p.optional_country || 'n/a'} · retention {p.retention_policy_days}d
              </div>
            </div>
            <Badge className="bg-slate-100 text-slate-800 border">{p.status}</Badge>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── new project wizard ────────────────────────────────────────────────────

function NewProjectPane({ onCreated, onOpenTrust }) {
  const [form, setForm] = useState({
    project_name: '',
    optional_source_system_name: '',
    optional_business_type: '',
    optional_country: '',
    optional_default_currency: '',
    retention_policy_days: 30,
  });
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!ack) { toast.error('Please acknowledge the data-protection notice.'); return; }
    if (!form.project_name.trim()) { toast.error('Project name is required.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/migration/projects`, {
        ...form,
        retention_policy_days: Number(form.retention_policy_days),
      });
      toast.success('Migration project created');
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-slate-200 max-w-2xl" data-testid="new-project-pane">
      <CardHeader>
        <CardTitle>New Migration Project</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormRow label="Project name *">
          <Input value={form.project_name} onChange={update('project_name')} data-testid="input-project-name" />
        </FormRow>
        <div className="grid grid-cols-2 gap-4">
          <FormRow label="Source system (optional)">
            <Input value={form.optional_source_system_name} onChange={update('optional_source_system_name')} placeholder="e.g. Legacy ERP" />
          </FormRow>
          <FormRow label="Business type (optional)">
            <Input value={form.optional_business_type} onChange={update('optional_business_type')} placeholder="e.g. Retail Distribution" />
          </FormRow>
          <FormRow label="Country (optional)">
            <Input value={form.optional_country} onChange={update('optional_country')} placeholder="e.g. Bahrain" />
          </FormRow>
          <FormRow label="Default currency (optional)">
            <Input value={form.optional_default_currency} onChange={update('optional_default_currency')} placeholder="e.g. BHD" />
          </FormRow>
        </div>
        <FormRow label="Raw-data retention policy">
          <select
            value={form.retention_policy_days}
            onChange={update('retention_policy_days')}
            className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm"
            data-testid="select-retention"
          >
            <option value={30}>30 days (default)</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </FormRow>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="checkbox-ack" />
          <span>
            I acknowledge that raw uploaded files are stored in tenant-isolated staging, are never used to train shared AI,
            and that I remain the sole owner of this data. See{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onOpenTrust?.(); }}
              className="text-emerald-700 hover:text-emerald-900 underline font-medium"
              data-testid="ack-trust-center-link"
            >
              Trust Center
            </button>{' '}
            for details.
          </span>
        </label>
        <div className="flex justify-end pt-2">
          <Button onClick={submit} disabled={submitting} data-testid="submit-create-project" className="bg-emerald-600 hover:bg-emerald-700">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-2" />}
            Create Migration Project
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

// ── dashboard summary ─────────────────────────────────────────────────────

function DashboardPane() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/migration/dashboard-summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  if (!summary) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="dashboard-pane">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total Projects" value={summary.total_projects} Icon={FolderKanban} />
        <StatCard label="Awaiting Action" value={summary.projects_awaiting_action} Icon={ClipboardList} />
        <StatCard label="Phase Live" value={summary.phase_live} Icon={CheckCircle2} tint="emerald" />
      </div>
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-4 text-sm text-slate-700">
          <div className="font-semibold mb-1">What happens next</div>
          <div>{summary.phase_next}</div>
        </CardContent>
      </Card>
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4 text-sm text-emerald-800 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          No staff access currently active. Your data is not being viewed by Konnect personnel.
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, Icon, tint = 'slate' }) {
  const tints = {
    slate: 'bg-white border-slate-200',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <Card className={tints[tint]}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-1">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        <div className="text-3xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

// ── access ledger ────────────────────────────────────────────────────────

function AccessLedgerPane() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/migration/access-ledger`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="access-ledger-pane">
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4 text-sm text-emerald-800 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          <strong>No staff access active</strong> — no Konnect personnel currently have permission to view your data.
        </CardContent>
      </Card>
      <h2 className="text-lg font-semibold text-slate-900">Recent Activity</h2>
      {events.length === 0 && (
        <div className="text-sm text-slate-500 p-6 text-center border border-dashed border-slate-300 rounded-lg">
          No events yet. All important actions on your migration projects appear here.
        </div>
      )}
      {events.map((e) => (
        <Card key={e._id} className="border-slate-200">
          <CardContent className="p-3 flex items-start gap-3">
            <ClipboardList className="w-4 h-4 mt-0.5 text-slate-400" />
            <div className="flex-1">
              <div className="text-sm text-slate-800">{e.customer_visible_description}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {e.event_type} · {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── data sources (Phase A.2) ──────────────────────────────────────────────

function DataSourcesPane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/migration/projects`);
      setProjects(data.projects || []);
      if (!activeProject && data.projects?.length) {
        setActiveProject(data.projects[0]._id);
      }
    } catch { /* toast handled elsewhere */ }
  }, [activeProject, setActiveProject]);

  const loadSources = useCallback(async (projectId) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${projectId}/sources`);
      setSources(data.sources || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { if (activeProject) loadSources(activeProject); }, [activeProject, loadSources]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeProject) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'json'].includes(ext)) {
      toast.error(`Unsupported extension .${ext}. Allowed: csv, xlsx, json`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/sources`, fd);
      const s = data.data_source;
      toast.success(`Uploaded '${s.original_file_name}' v${s.version_number} — ${s.upload_status}`);
      loadSources(activeProject);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openPreview = async (source) => {
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/sources/${source._id}/preview`);
      setPreview({ source, ...data.preview });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
  };

  const activate = async (source) => {
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/sources/${source._id}/activate`);
      toast.success(`Activated v${source.version_number}`);
      loadSources(activeProject);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const del = async (source) => {
    if (!window.confirm(`Delete '${source.original_file_name}' v${source.version_number}? Audit history is preserved.`)) return;
    try {
      await axios.delete(`${API}/api/migration/projects/${activeProject}/sources/${source._id}`);
      toast.success('Source deleted');
      loadSources(activeProject);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="sources-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Data Sources</h2>
        <select
          value={activeProject || ''}
          onChange={(e) => setActiveProject(e.target.value)}
          className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
          data-testid="active-project-select"
        >
          <option value="">Select a project…</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
          ))}
        </select>
      </div>

      {!activeProject && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-slate-500">Pick a project above to upload data sources.</CardContent>
        </Card>
      )}

      {activeProject && (
        <>
          <Card className="border-2 border-dashed border-emerald-300 bg-emerald-50/40">
            <CardContent className="p-6 flex items-center gap-4">
              <Upload className="w-8 h-8 text-emerald-600" />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Upload legacy export</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  CSV, XLSX or JSON · max 200 MB · files are quarantined + signature-checked before staging
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.json"
                onChange={handleUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="upload-btn"
              >
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4 mr-2" />Choose file</>}
              </Button>
            </CardContent>
          </Card>

          {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

          {!loading && sources.length === 0 && (
            <div className="text-sm text-slate-500 p-6 text-center border border-dashed border-slate-300 rounded-lg">
              No sources uploaded yet.
            </div>
          )}

          {sources.map((s) => <SourceRow key={s._id} source={s} onPreview={openPreview} onActivate={activate} onDelete={del} />)}

          {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
        </>
      )}
    </div>
  );
}

function SourceRow({ source, onPreview, onActivate, onDelete }) {
  const statusColor = {
    promoted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    quarantined: 'bg-amber-100 text-amber-800 border-amber-300',
    rejected: 'bg-rose-100 text-rose-800 border-rose-300',
    deleted: 'bg-slate-200 text-slate-500 border-slate-300',
  }[source.upload_status] || 'bg-slate-100 text-slate-700 border-slate-200';
  const StatusIcon = source.upload_status === 'promoted' ? CheckCircle2 : source.upload_status === 'rejected' ? XCircle : AlertTriangle;
  const isDeleted = source.upload_status === 'deleted';

  return (
    <Card className="border-slate-200" data-testid={`source-row-${source._id}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <FileText className="w-5 h-5 text-slate-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 truncate">{source.original_file_name}</span>
            <Badge className="bg-slate-100 text-slate-700 border text-[10px]">v{source.version_number}</Badge>
            {source.is_active_version && !isDeleted && <Badge className="bg-emerald-600 text-white text-[10px]">ACTIVE</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">
            {source.source_format.toUpperCase()} · {(source.byte_size / 1024).toFixed(1)} KB · sha256 {source.sha256_checksum.slice(0, 12)}…
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Validation: <strong>{source.validation_status}</strong> · Scan: <strong>{source.malware_scan_status}</strong>
          </div>
        </div>
        <Badge className={`border ${statusColor}`}>
          <StatusIcon className="w-3.5 h-3.5 mr-1" />
          {source.upload_status}
        </Badge>
        {source.upload_status === 'promoted' && (
          <>
            <Button size="sm" variant="outline" onClick={() => onPreview(source)} data-testid={`preview-${source._id}`}>
              <Eye className="w-3.5 h-3.5 mr-1" />Preview
            </Button>
            {!source.is_active_version && (
              <Button size="sm" variant="outline" onClick={() => onActivate(source)} data-testid={`activate-${source._id}`}>
                Activate
              </Button>
            )}
          </>
        )}
        {!isDeleted && (
          <Button size="sm" variant="ghost" onClick={() => onDelete(source)} data-testid={`delete-${source._id}`}>
            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PreviewModal({ preview, onClose }) {
  const isXlsx = preview.sheets !== undefined;
  const isJson = preview.shape !== undefined;
  const sheet = isXlsx ? preview.sheets[0] : null;
  const headers = isXlsx ? sheet?.headers || [] : preview.headers || [];
  const rows = isXlsx ? sheet?.rows_masked || [] : preview.rows_masked || [];
  const total = isXlsx ? sheet?.total_rows : (preview.total_rows_estimated || preview.total_records);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[92vh] sm:max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 sm:p-4 border-b border-slate-200 flex items-start sm:items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-sm sm:text-base truncate">{preview.source?.original_file_name} — masked preview</div>
            <div className="text-[11px] sm:text-xs text-slate-500">
              First 20 rows shown. Total: {total || 0}. Sensitive columns are masked. Formulas neutralised.
              {isJson && ` Shape: ${preview.shape}.`}
              {isXlsx && ` Sheets: ${preview.sheet_count}.`}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </div>
        <div className="overflow-auto flex-1 p-3 sm:p-4">
          <table className="min-w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="border border-slate-300 px-2 py-1 text-left font-semibold text-slate-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="border border-slate-200 px-2 py-1 font-mono text-slate-800">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ── Discovery & Profiling (Phase A.3) ─────────────────────────────────────

function DiscoveryPane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    });
  }, [activeProject, setActiveProject]);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/profile`);
      setEntities(data.entities || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load profile');
    } finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { load(); }, [load]);

  const severityColor = {
    critical: 'bg-rose-100 text-rose-800 border-rose-300',
    high: 'bg-rose-100 text-rose-700 border-rose-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
  };

  return (
    <div className="space-y-4" data-testid="discovery-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Discovery & Profiling</h2>
        <div className="flex gap-2 items-center">
          <select
            value={activeProject || ''}
            onChange={(e) => setActiveProject(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
            data-testid="discovery-project-select"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="refresh-discovery">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && entities.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-slate-500 space-y-2">
            <Search className="w-10 h-10 mx-auto text-slate-300" />
            <div>No profiled entities yet.</div>
            <div className="text-xs">Upload a source in the Data Sources section — profiling runs automatically in the background.</div>
          </CardContent>
        </Card>
      )}

      {entities.map((e) => (
        <Card key={e._id} className="border-slate-200" data-testid={`entity-${e._id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{e.source_entity_name}</CardTitle>
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                    → {e.suggested_entity_type}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {e.row_count} rows · {e.column_count} cols · {e.duplicate_row_count} dup · PK candidate:{' '}
                  <span className="font-mono">{e.detected_primary_key || '—'}</span>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <ScoreBadge label="Quality" value={e.data_quality_score} />
                <ScoreBadge label="Confidence" value={e.inference_confidence_score} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <button
              onClick={() => setExpanded((s) => ({ ...s, [e._id]: !s[e._id] }))}
              className="text-xs text-emerald-700 hover:text-emerald-900 font-medium"
              data-testid={`toggle-${e._id}`}
            >
              {expanded[e._id] ? '▾ Hide' : '▸ Show'} {e.columns?.length || 0} column profiles
            </button>
            {expanded[e._id] && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Header', 'Type', 'Semantic', 'Null%', 'Cardinality', 'Confidence', 'Flags'].map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 border border-slate-200 font-semibold text-slate-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(e.columns || []).map((c) => (
                      <tr key={c._id}>
                        <td className="px-2 py-1.5 border border-slate-200 font-mono">{c.original_header}</td>
                        <td className="px-2 py-1.5 border border-slate-200"><Badge className="bg-slate-100 text-slate-800 border text-[10px]">{c.inferred_data_type}</Badge></td>
                        <td className="px-2 py-1.5 border border-slate-200 text-slate-600">{c.inferred_semantic_type}</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">{(c.null_rate * 100).toFixed(0)}%</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">{c.cardinality}</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right"><ScoreBadge value={c.confidence_score} compact /></td>
                        <td className="px-2 py-1.5 border border-slate-200">
                          {c.is_identifier_candidate && <span className="text-[10px] mr-1 text-purple-700">ID</span>}
                          {c.is_sensitive_data_candidate && <span className="text-[10px] mr-1 text-rose-700">SENSITIVE</span>}
                          {c.detected_date_format && <span className="text-[10px] mr-1 text-blue-700">{c.detected_date_format}</span>}
                          {c.detected_currency && <span className="text-[10px] mr-1 text-emerald-700">{c.detected_currency}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(e.findings || []).length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Data Quality Findings</div>
                {(e.findings || []).map((f) => (
                  <div key={f._id} className="flex items-start gap-2 bg-slate-50 rounded-md p-2 border border-slate-200">
                    <Badge className={`${severityColor[f.severity] || severityColor.low} border text-[10px]`}>{f.severity}</Badge>
                    <div className="flex-1">
                      <div className="text-sm text-slate-800 font-medium">{f.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{f.description}</div>
                      <div className="text-xs text-emerald-700 mt-0.5">Suggested: {f.suggested_action}</div>
                    </div>
                    {f.requires_client_action && <Badge className="bg-amber-100 text-amber-800 border text-[10px]">CLIENT ACTION</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ScoreBadge({ label, value, compact = false }) {
  const color = value >= 80 ? 'text-emerald-700 bg-emerald-50' : value >= 50 ? 'text-amber-700 bg-amber-50' : 'text-rose-700 bg-rose-50';
  if (compact) {
    return <span className={`inline-block px-1.5 py-0.5 rounded font-mono ${color}`}>{value ?? 0}</span>;
  }
  return (
    <div className="text-center">
      <div className={`px-2 py-1 rounded font-mono text-sm font-bold ${color}`}>{value ?? 0}</div>
      <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">{label}</div>
    </div>
  );
}


// ── Phase A.4: Mapping Workbench ──────────────────────────────────────────

function MappingPane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    });
  }, [activeProject, setActiveProject]);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/mappings`);
      setMappings(data.mappings || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mappings');
    } finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!activeProject) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/mappings/generate`);
      toast.success(`Generated ${data.suggestions_written} suggestion(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const decide = async (m, decision) => {
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/mappings/${m._id}/decision`, {
        decision,
      });
      toast.success(`Mapping ${decision}d`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const filtered = mappings.filter((m) => filter === 'all' || m.status === filter);
  const counts = mappings.reduce((acc, m) => { acc[m.status] = (acc[m.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="mapping-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Mapping Workbench</h2>
        <div className="flex gap-2 items-center">
          <select
            value={activeProject || ''}
            onChange={(e) => setActiveProject(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
            data-testid="mapping-project-select"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="mapping-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button size="sm" onClick={generate} disabled={generating || !activeProject}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="mapping-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                        : <><Sparkles className="w-4 h-4 mr-2" />Generate suggestions</>}
          </Button>
        </div>
      </div>

      {mappings.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="mapping-filter-row">
          {['all', 'suggested', 'approved', 'rejected'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    data-testid={`mapping-filter-${f}`}
                    className={`text-xs px-3 py-1 rounded-full border ${
                      filter === f ? 'bg-slate-900 text-white border-slate-900'
                                   : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {f} {f !== 'all' && counts[f] ? `(${counts[f]})` : ''}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && mappings.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-slate-500 space-y-2">
            <Layers className="w-10 h-10 mx-auto text-slate-300" />
            <div>No mapping suggestions yet.</div>
            <div className="text-xs">Upload + profile a source, then hit <span className="font-semibold">Generate suggestions</span>.</div>
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-xs border-collapse" data-testid="mapping-table">
            <thead className="bg-slate-50">
              <tr>
                {['Source Column', 'Type', 'Entity', '→', 'Target', 'Method', 'Confidence', 'Review', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left px-2 py-2 border-b border-slate-200 font-semibold text-slate-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m._id} data-testid={`mapping-row-${m._id}`} className="hover:bg-slate-50">
                  <td className="px-2 py-2 border-b border-slate-100 font-mono">{m._source_header || '—'}</td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    <Badge className="bg-slate-100 text-slate-700 border text-[10px]">{m._source_type || '—'}</Badge>
                  </td>
                  <td className="px-2 py-2 border-b border-slate-100 text-slate-600">{m._source_entity_name || '—'}</td>
                  <td className="px-2 py-2 border-b border-slate-100 text-slate-400">→</td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    <span className="font-mono text-slate-800">{m.target_canonical_object_key}.{m.target_canonical_field_key}</span>
                  </td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{m.mapping_method}</Badge>
                  </td>
                  <td className="px-2 py-2 border-b border-slate-100 text-right"><ScoreBadge value={m.confidence_score} compact /></td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    {m.requires_human_review ? <Badge className="bg-amber-100 text-amber-800 border text-[10px]">REVIEW</Badge>
                                              : <Badge className="bg-emerald-100 text-emerald-800 border text-[10px]">AUTO-OK</Badge>}
                  </td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    {m.status === 'approved' && <Badge className="bg-emerald-100 text-emerald-800 border text-[10px]">approved</Badge>}
                    {m.status === 'rejected' && <Badge className="bg-rose-100 text-rose-800 border text-[10px]">rejected</Badge>}
                    {m.status === 'suggested' && <Badge className="bg-slate-100 text-slate-700 border text-[10px]">suggested</Badge>}
                  </td>
                  <td className="px-2 py-2 border-b border-slate-100">
                    {m.status === 'suggested' ? (
                      <div className="flex gap-1">
                        <button onClick={() => decide(m, 'approve')} title={m.reasoning_summary}
                                data-testid={`mapping-approve-${m._id}`}
                                className="text-emerald-700 hover:bg-emerald-50 p-1 rounded"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => decide(m, 'reject')}
                                data-testid={`mapping-reject-${m._id}`}
                                className="text-rose-700 hover:bg-rose-50 p-1 rounded"><XCircle className="w-4 h-4" /></button>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length === 0 && mappings.length > 0 && (
        <div className="text-sm text-slate-500 p-4 text-center">No mappings match filter &ldquo;{filter}&rdquo;.</div>
      )}
    </div>
  );
}

// ── Phase A.4: Canonical Registry (browse canonical objects + fields) ──────

function CanonicalRegistryPane() {
  const [registry, setRegistry] = useState({ objects: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/migration/canonical-registry`)
      .then((r) => setRegistry(r.data || { objects: [] }))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load registry'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="canonical-registry-pane">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Canonical Business Graph</h2>
        <Badge className="bg-slate-900 text-white">{registry.count || registry.objects?.length || 0} objects</Badge>
      </div>
      <p className="text-sm text-slate-600">
        Universal translation targets — every source column maps to a canonical object.field. Fields flagged
        <span className="mx-1 inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold">HIGH-RISK</span>
        always require human approval, regardless of confidence.
      </p>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {registry.objects?.map((o) => (
        <Card key={o.key} className="border-slate-200" data-testid={`canonical-object-${o.key}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{o.display}</CardTitle>
                  <Badge className="bg-slate-100 text-slate-700 border text-[10px]">{o.domain}</Badge>
                  <Badge className={`text-[10px] border ${
                    o.sensitivity === 'critical' ? 'bg-rose-100 text-rose-800 border-rose-200'
                      : o.sensitivity === 'high' ? 'bg-amber-100 text-amber-800 border-amber-200'
                      : o.sensitivity === 'medium' ? 'bg-blue-100 text-blue-800 border-blue-200'
                      : 'bg-emerald-100 text-emerald-800 border-emerald-200'}`}>
                    {o.sensitivity} sensitivity
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono">{o.key}</div>
              </div>
              <button onClick={() => setExpanded((s) => ({ ...s, [o.key]: !s[o.key] }))}
                      data-testid={`toggle-canonical-${o.key}`}
                      className="text-xs text-emerald-700 hover:text-emerald-900 font-medium">
                {expanded[o.key] ? '▾ Hide' : '▸ Show'} {o.fields?.length || 0} fields
              </button>
            </div>
          </CardHeader>
          {expanded[o.key] && (
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Field', 'Type', 'Semantic', 'Required', 'Flags'].map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 border border-slate-200 font-semibold text-slate-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {o.fields.map((f) => (
                      <tr key={f.key}>
                        <td className="px-2 py-1.5 border border-slate-200 font-mono">{f.key}</td>
                        <td className="px-2 py-1.5 border border-slate-200"><Badge className="bg-slate-100 text-slate-700 border text-[10px]">{f.type}</Badge></td>
                        <td className="px-2 py-1.5 border border-slate-200 text-slate-600">{f.semantic || '—'}</td>
                        <td className="px-2 py-1.5 border border-slate-200">{f.required ? '✓' : ''}</td>
                        <td className="px-2 py-1.5 border border-slate-200">
                          {f.is_high_risk && <Badge className="bg-rose-100 text-rose-800 border text-[10px] mr-1">HIGH-RISK</Badge>}
                          {f.id_key && <span className="text-[10px] mr-1 text-purple-700">ID</span>}
                          {f.relationship_key && <span className="text-[10px] mr-1 text-blue-700">FK</span>}
                          {f.financial_sensitive && <span className="text-[10px] mr-1 text-emerald-700">$$</span>}
                          {f.sensitive_identity && <span className="text-[10px] mr-1 text-rose-700">PII</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

// ── Phase A.5: Gaps & Proposals ────────────────────────────────────────────

function GapsPane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    });
  }, [activeProject, setActiveProject]);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API}/api/migration/projects/${activeProject}/gaps`),
        axios.get(`${API}/api/migration/projects/${activeProject}/proposals`),
      ]);
      setGaps(g.data.gaps || []);
      setProposals(p.data.proposals || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!activeProject) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/gaps/generate`);
      toast.success(`Generated ${data.gaps_written} gap(s) · ${data.proposals_written} proposal(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const decideProposal = async (p, decision) => {
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/proposals/${p._id}/decision`, { decision });
      toast.success(`Proposal ${decision}d`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const promoteToAddon = async (p) => {
    if (!window.confirm(`Promote "${p.proposal_name}" into a new Add-On Module?\n\nThe module will appear in the sidebar under "Add-On Modules" as a stub scaffold. You can configure fields and workflows from the module page.`)) return;
    try {
      const { data } = await axios.post(
        `${API}/api/migration/projects/${activeProject}/proposals/${p._id}/promote-to-addon`,
        { icon_key: 'Blocks' }
      );
      if (data.created === false) {
        toast.info('Add-On Module already exists for this proposal.');
      } else {
        toast.success(`Add-On Module "${data.module.display_name}" created.`);
      }
      // Broadcast so the sidebar picks up the new module without a hard reload
      window.dispatchEvent(new CustomEvent('konnect:addon-modules-changed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Promotion failed');
    }
  };

  const severityColor = (s) => ({
    critical: 'bg-rose-100 text-rose-800 border-rose-300',
    high: 'bg-rose-100 text-rose-700 border-rose-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
  }[s] || 'bg-slate-100 text-slate-700 border-slate-200');

  return (
    <div className="space-y-4" data-testid="gaps-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Gaps & Custom Metadata Proposals</h2>
        <div className="flex gap-2 items-center">
          <select
            value={activeProject || ''}
            onChange={(e) => setActiveProject(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
            data-testid="gaps-project-select"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="gaps-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button size="sm" onClick={generate} disabled={generating || !activeProject}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="gaps-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analysing…</>
                        : <><Sparkles className="w-4 h-4 mr-2" />Run gap analysis</>}
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="gaps-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileWarning className="w-4 h-4" />Gaps ({gaps.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {gaps.length === 0 && <div className="text-xs text-slate-500 text-center p-4">No gaps &mdash; either you haven&apos;t run analysis or coverage is 100%.</div>}
            {gaps.map((g) => (
              <div key={g._id} className="p-2 border rounded-md bg-slate-50 border-slate-200" data-testid={`gap-${g._id}`}>
                <div className="flex items-start gap-2">
                  <Badge className={`${severityColor(g.severity)} border text-[10px]`}>{g.severity}</Badge>
                  <Badge className="bg-white text-slate-600 border text-[10px]">{g.gap_type}</Badge>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{g.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{g.description}</div>
                  </div>
                  {g.requires_client_action && <Badge className="bg-amber-100 text-amber-800 border text-[10px]">CLIENT ACTION</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="proposals-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" />Metadata Proposals ({proposals.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {proposals.length === 0 && <div className="text-xs text-slate-500 text-center p-4">No proposals yet.</div>}
            {proposals.map((p) => (
              <div key={p._id} className="p-2 border rounded-md bg-slate-50 border-slate-200" data-testid={`proposal-${p._id}`}>
                <div className="flex items-start gap-2">
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{p.proposal_type}</Badge>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{p.proposal_name}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{p.reason}</div>
                    <div className="text-[10px] text-slate-400 mt-1">Confidence: {p.confidence_score} · Status: {p.approval_status}</div>
                  </div>
                  {p.approval_status === 'proposed' && (
                    <div className="flex gap-1">
                      <button onClick={() => decideProposal(p, 'approve')}
                              data-testid={`proposal-approve-${p._id}`}
                              className="text-emerald-700 hover:bg-emerald-50 p-1 rounded"><CheckCircle2 className="w-4 h-4" /></button>
                      <button onClick={() => decideProposal(p, 'reject')}
                              data-testid={`proposal-reject-${p._id}`}
                              className="text-rose-700 hover:bg-rose-50 p-1 rounded"><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                  {p.approval_status === 'approved' && ['custom_object', 'workflow', 'integration'].includes(p.proposal_type) && (
                    <button
                      onClick={() => promoteToAddon(p)}
                      data-testid={`proposal-promote-${p._id}`}
                      className="text-[10px] font-medium px-2 py-1 rounded-md bg-sky-100 text-sky-800 hover:bg-sky-200 border border-sky-200 flex items-center gap-1"
                      title="Create a stub Add-On Module from this proposal"
                    >
                      <Sparkles className="w-3 h-3" /> Promote to Add-On
                    </button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Phase A.5: Coverage Report ────────────────────────────────────────────

function CoveragePane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    });
  }, [activeProject, setActiveProject]);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/coverage`);
      setCoverage(data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="coverage-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Coverage Report</h2>
        <div className="flex gap-2 items-center">
          <select
            value={activeProject || ''}
            onChange={(e) => setActiveProject(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
            data-testid="coverage-project-select"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="coverage-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && coverage && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="coverage-scores">
            <StatCard label="Overall Coverage" value={`${coverage.overall_coverage_percent}%`} Icon={BarChart3} tint="emerald" />
            <StatCard label="Columns Mapped" value={`${coverage.columns_mapped}/${coverage.columns_total}`} Icon={Layers} tint="blue" />
            <StatCard label="Approved" value={coverage.mappings_approved} Icon={CheckCircle2} tint="emerald" />
            <StatCard label="Pending Review" value={coverage.mappings_pending_review} Icon={AlertTriangle} tint="amber" />
          </div>
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Per-Entity Coverage</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="coverage-entity-table">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Entity', 'Columns', 'Mapped', 'Approved', 'Coverage %'].map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 border border-slate-200 font-semibold text-slate-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(coverage.per_entity || []).map((e) => (
                      <tr key={e.entity_id}>
                        <td className="px-2 py-1.5 border border-slate-200">{e.entity_name} <span className="text-slate-400">→ {e.canonical_target}</span></td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">{e.columns_total}</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">{e.columns_mapped}</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">{e.mappings_approved}</td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right"><ScoreBadge value={e.coverage_percent} compact /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(coverage.canonical_objects_covered || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Canonical objects covered</div>
                  <div className="flex flex-wrap gap-1" data-testid="coverage-canonical-objects">
                    {coverage.canonical_objects_covered.map((c) => (
                      <Badge key={c} className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!loading && !coverage && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-slate-500">Select a project to view its coverage report.</CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Phase A.6: Trust Center ───────────────────────────────────────────────

function TrustCenterPane({ activeProject, setActiveProject }) {
  const [projects, setProjects] = useState([]);
  const [trust, setTrust] = useState(null);
  const [supportSessions, setSupportSessions] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    });
  }, [activeProject, setActiveProject]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = activeProject
        ? `${API}/api/migration/trust-center?project_id=${activeProject}`
        : `${API}/api/migration/trust-center`;
      const [t, s, f] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/api/migration/support-access/active`),
        axios.get(`${API}/api/migration/feature-flags/effective`),
      ]);
      setTrust(t.data);
      setSupportSessions(s.data.active_sessions || []);
      setFlags(f.data.flags || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { load(); }, [load]);

  const productionFlag = flags.find((f) => f.key === 'production_release_enabled');
  const productionEnabled = productionFlag?.effective_enabled === true;

  const setProductionFlag = async (enabled) => {
    setToggling(true);
    try {
      await axios.post(
        `${API}/api/migration/feature-flags/production_release_enabled/set`,
        { enabled },
      );
      toast.success(enabled
        ? 'Production writes are now ENABLED for this tenant'
        : 'Production writes are now DISABLED for this tenant');
      setShowConfirm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setToggling(false); }
  };

  const revoke = async (session_id) => {
    try {
      await axios.post(`${API}/api/migration/support-access/${session_id}/revoke`);
      toast.success('Support access revoked');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="trust-center-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Trust Center</h2>
        <div className="flex gap-2 items-center">
          <select
            value={activeProject || ''}
            onChange={(e) => setActiveProject(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
            data-testid="trust-project-select"
          >
            <option value="">All projects (tenant-wide)</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="trust-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && trust && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="border-emerald-200 bg-emerald-50" data-testid="trust-isolation-card">
              <CardContent className="p-4 flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-700" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Tenant Isolation</div>
                  <div className="text-sm text-emerald-900 mt-1">{trust.isolation_status}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50" data-testid="trust-encryption-card">
              <CardContent className="p-4 flex items-start gap-3">
                <Lock className="w-6 h-6 text-blue-700" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-blue-800 font-semibold">Encryption at Rest</div>
                  <div className="text-sm text-blue-900 mt-1">{trust.encryption_status}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50" data-testid="trust-retention-card">
              <CardContent className="p-4 flex items-start gap-3">
                <ClipboardList className="w-6 h-6 text-amber-700" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-amber-800 font-semibold">Retention Policy</div>
                  <div className="text-sm text-amber-900 mt-1">{trust.retention_summary}</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Compliance Snapshot</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-slate-500">Projects</span><div className="font-semibold text-slate-800">{trust.projects_count}</div></div>
                <div><span className="text-slate-500">Sources uploaded</span><div className="font-semibold text-slate-800">{trust.sources_count}</div></div>
                <div><span className="text-slate-500">Ledger events</span><div className="font-semibold text-slate-800">{trust.ledger_events_count}</div></div>
                <div><span className="text-slate-500">Sources past retention</span><div className={`font-semibold ${trust.sources_past_retention > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{trust.sources_past_retention}</div></div>
              </div>
            </CardContent>
          </Card>

          {/* v2.12 — Production Writes opt-in card */}
          <Card
            className={`border-2 ${productionEnabled ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
            data-testid="production-writes-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className={`w-5 h-5 ${productionEnabled ? 'text-emerald-700' : 'text-amber-700'}`} />
                  Production Writes
                </CardTitle>
                <Badge
                  className={`text-[11px] border ${
                    productionEnabled
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : 'bg-slate-100 text-slate-700 border-slate-300'
                  }`}
                  data-testid="production-writes-status-pill"
                >
                  {productionEnabled ? '● ENABLED' : '○ DISABLED'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-700 leading-relaxed">
                When enabled, an approved Release Manifest can be <b>executed</b> to write migrated rows into your live
                {' '}<span className="font-mono text-slate-800">business_records_*</span> collections.
                Every executed release stores its <b>reverse plan</b> so a one-click Rollback is always available.
              </p>
              <div className="text-xs text-slate-500 bg-white/70 rounded-md p-2 border border-slate-200">
                <div className="font-semibold text-slate-700 mb-1">Guardrails still enforced:</div>
                <ul className="space-y-1 list-disc list-inside">
                  <li>All 3 UAT sign-offs (owner + finance + steward) required before Execute is callable</li>
                  <li>Reverse plan pre-computed for every write</li>
                  <li>Target collection prefix strictly <span className="font-mono">business_records_</span></li>
                  <li>Tenant-scoped — no cross-tenant writes possible</li>
                </ul>
              </div>
              <div className="flex items-center justify-between gap-3 pt-1">
                <div className="text-xs text-slate-600">
                  Tenant owner only. This action is audit-logged to the Access Ledger.
                </div>
                {productionEnabled ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setProductionFlag(false)}
                    disabled={toggling}
                    className="text-rose-700 border-rose-300 hover:bg-rose-50"
                    data-testid="production-writes-disable-btn"
                  >
                    {toggling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disabling…</> : 'Disable production writes'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setShowConfirm(true)}
                    disabled={toggling}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="production-writes-enable-btn"
                  >
                    Enable production writes
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Enable confirmation dialog */}
          {showConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
              onClick={() => !toggling && setShowConfirm(false)}
              data-testid="production-writes-confirm-modal"
            >
              <Card className="w-full max-w-lg border-emerald-300" onClick={(e) => e.stopPropagation()}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Enable Production Writes?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    This allows Migration Studio to <b>write real rows into your production business tables</b>
                    {' '}(<span className="font-mono">business_records_*</span>).
                    Reversible via one-click <b>Rollback</b> on every executed release.
                  </p>
                  <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-2 border border-slate-200 space-y-1">
                    <div>✓ UAT sign-off still required before every Execute</div>
                    <div>✓ Reverse plan pre-computed — Rollback always available</div>
                    <div>✓ Tenant-scoped — no cross-tenant writes</div>
                    <div>✓ Audit-logged to the Access Ledger</div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowConfirm(false)}
                      disabled={toggling}
                      data-testid="production-writes-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setProductionFlag(true)}
                      disabled={toggling}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="production-writes-confirm-btn"
                    >
                      {toggling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enabling…</> : 'Yes, enable'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4" />Active Konnect Support Access ({supportSessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {supportSessions.length === 0 ? (
                <div className="text-xs text-slate-500 p-3 text-center bg-slate-50 rounded-md">
                  No active Konnect staff sessions on your tenant. Every staff access requires your explicit approval.
                </div>
              ) : (
                <div className="space-y-2">
                  {supportSessions.map((s) => (
                    <div key={s._id} className="flex items-center justify-between p-2 border rounded-md bg-amber-50 border-amber-200" data-testid={`support-session-${s._id}`}>
                      <div className="text-xs">
                        <div className="font-semibold text-slate-800">Session {s._id.slice(0, 8)}</div>
                        <div className="text-slate-600">Started: {new Date(s.started_at).toLocaleString()} · Expires: {new Date(s.expires_at).toLocaleString()}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => revoke(s._id)}
                              data-testid={`support-revoke-${s._id}`}
                              className="text-rose-700 border-rose-300 hover:bg-rose-50">
                        Revoke now
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

